// src/sectors/pilates/sessions/components/SessionDetailDialog/SessionDetailDialog.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

import { clean, upper, hm, pickParticipants } from './sessionDetail.utils';
import { SessionHeroCard } from './SessionHeroCard';
import { SessionParticipantsCard } from './SessionParticipantsCard';
import { SessionNotesCard } from './SessionNotesCard';
import { SessionDetailMenu } from './SessionDetailMenu';
import { AttendanceMarkModal } from 'sectors/pilates/attendance/mark/AttendanceMarkModal/AttendanceMarkModal';

export default function SessionDetailDialog({
  open,
  onClose,
  sessionId,
  sessionSnapshot,
  onEdit,
  onPayment,
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const borderSoft = `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1)}`;

  const [loading, setLoading] = React.useState(false);
  const [detail, setDetail] = React.useState(null);

  // notes
  const [notesLoading, setNotesLoading] = React.useState(false);
  const [notes, setNotes] = React.useState([]);

  // composer
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [type, setType] = React.useState('NOTE'); // NOTE | REMINDER
  const [text, setText] = React.useState('');

  // reminder
  const [reminderMode, setReminderMode] = React.useState('BEFORE_MIN'); // BEFORE_MIN | AT_TIME
  const [beforeMin, setBeforeMin] = React.useState(240);
  const [reminderAtLocal, setReminderAtLocal] = React.useState('');
  const [channel, setChannel] = React.useState('IN_APP');

  const [saving, setSaving] = React.useState(false);

  // attendance modal
  const [attOpen, setAttOpen] = React.useState(false);
  const [attTarget, setAttTarget] = React.useState(null); // null => seans, object => participant

  // menu
  const [moreEl, setMoreEl] = React.useState(null);
  const moreOpen = Boolean(moreEl);

  const s = detail || sessionSnapshot || null;
  const ps = React.useMemo(() => pickParticipants(s), [s]);

  const billingMode = upper(s?.billingMode);
  const isPaid = billingMode === 'PAID';
  const isPackage = billingMode === 'PACKAGE';

  const cap = Number(s?.capacity ?? 0) || 0;
  const booked = Number(s?.participantCount ?? ps.length ?? 0) || 0;

  const pCounts = React.useMemo(() => {
    return ps.reduce(
      (acc, p) => {
        const st = upper(p.attendanceStatus);
        if (st === 'ATTENDED') acc.attended += 1;
        else if (st === 'NO_SHOW') acc.noShow += 1;
        else if (st === 'CANCELLED') acc.cancelled += 1;
        else acc.pending += 1;
        return acc;
      },
      { attended: 0, noShow: 0, cancelled: 0, pending: 0 }
    );
  }, [ps]);

  const reloadNotes = React.useCallback(async (sid) => {
    if (!sid) return;
    setNotesLoading(true);
    try {
      const list = await sessionsApi.listSessionNotes(sid);
      setNotes(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('[notes] list failed', e);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, []);

  React.useEffect(() => {
    let alive = true;

    async function run() {
      if (!open) return;

      setDetail(sessionSnapshot || null);

      const sid = sessionId || sessionSnapshot?.id || null;
      if (!sid) return;

      setLoading(true);
      try {
        const d = await sessionsApi.getSession(sid);
        if (!alive) return;
        setDetail(d);
      } catch (e) {
        // snapshot ile devam
      } finally {
        if (alive) setLoading(false);
      }

      await reloadNotes(sid);
    }

    // reset per open
    setComposerOpen(false);
    setType('NOTE');
    setText('');
    setReminderMode('BEFORE_MIN');
    setBeforeMin(240);
    setReminderAtLocal('');
    setChannel('IN_APP');
    setSaving(false);

    setAttOpen(false);
    setAttTarget(null);

    setMoreEl(null);

    run();
    return () => {
      alive = false;
    };
  }, [open, sessionId, sessionSnapshot, reloadNotes]);

  const computeBeforeMinFromAtTime = () => {
    const dayISO = s?.dayISO;
    const startMin = Number(s?.startMin ?? 0) || 0;
    if (!dayISO || !reminderAtLocal) return null;

    const hh = Math.floor(startMin / 60);
    const mm = startMin % 60;

    const sessionStartLocal = new Date(
      `${dayISO}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`
    );
    const reminderAt = new Date(reminderAtLocal);

    const diffMs = sessionStartLocal.getTime() - reminderAt.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (!Number.isFinite(diffMin) || diffMin <= 0) return null;
    return diffMin;
  };

  const addNoteOrReminder = async () => {
    const sid = s?.id ?? sessionId;
    if (!sid || saving) return;

    const noteText = clean(text);
    if (!noteText) return;

    let finalBeforeMin = null;

    if (type === 'REMINDER') {
      if (reminderMode === 'BEFORE_MIN') finalBeforeMin = Number(beforeMin) || null;
      else finalBeforeMin = computeBeforeMinFromAtTime();

      if (!finalBeforeMin || finalBeforeMin <= 0) return;
    }

    setSaving(true);
    try {
      const payload =
        type === 'NOTE'
          ? { type: 'NOTE', note: noteText }
          : {
              type: 'REMINDER',
              note: noteText,
              reminderBeforeMinutes: finalBeforeMin,
              reminderChannel: channel,
              // ✅ sadece kendisine gitsin
              // backend destekliyorsa: target: "SELF"
            };

      await sessionsApi.createSessionNote(sid, payload);

      setComposerOpen(false);
      setText('');
      await reloadNotes(sid);
    } catch (e) {
      console.error('[notes] create failed', e);
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async (noteId) => {
    const sid = s?.id ?? sessionId;
    if (!sid || !noteId) return;
    try {
      await sessionsApi.deleteSessionNote(sid, noteId);
      await reloadNotes(sid);
    } catch (e) {
      console.error('[notes] delete failed', e);
    }
  };

  const openAttendanceForSession = () => {
    setAttTarget(null);
    setAttOpen(true);
  };
  const openAttendanceForParticipant = (p) => {
    setAttTarget(p);
    setAttOpen(true);
  };

  const onAttendanceSave = async ({ cancelled, attended, note }) => {
    const sid = s?.id ?? sessionId;
    if (!sid) return;

    try {
      // ✅ burada API’ye bağlayacaksın
      // if (attTarget?.memberId) { ... } else { ... }

      const d = await sessionsApi.getSession(sid);
      setDetail(d);
    } catch (e) {
      console.error('[attendance] save failed', e);
    } finally {
      setAttOpen(false);
      setAttTarget(null);
    }
  };

  const openMore = (e) => setMoreEl(e.currentTarget);
  const closeMore = () => setMoreEl(null);

  const cancelSession = async () => {
    const sid = s?.id ?? sessionId;
    if (!sid) return;
    try {
      // ✅ endpoint varsa bağla: await sessionsApi.cancelSession(sid);
      const d = await sessionsApi.getSession(sid);
      setDetail(d);
    } catch (e) {
      console.error('[session] cancel failed', e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pb: 1,
        }}
      >
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <MDBox
            sx={{
              width: 42,
              height: 42,
              borderRadius: '16px',
              display: 'grid',
              placeItems: 'center',
              background: isDark
                ? alpha(theme.palette.info.main, 0.16)
                : alpha(theme.palette.info.main, 0.12),
              border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
              flex: '0 0 auto',
            }}
          >
            <Icon sx={{ opacity: 0.9 }}>info</Icon>
          </MDBox>

          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant="h6" sx={{ fontWeight: 1000, lineHeight: 1.1 }}>
              Seans Detayı
            </MDTypography>
            <MDTypography variant="caption" sx={{ opacity: 0.75, fontWeight: 800 }}>
              {s?.dayISO ? `${s.dayISO} • ${hm(s.startMin)}` : '—'}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Tooltip title="İşlemler">
            <IconButton
              size="small"
              onClick={openMore}
              sx={{
                borderRadius: 2,
                border: borderSoft,
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
              }}
            >
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          </Tooltip>

          <Tooltip title="Kapat">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                borderRadius: 2,
                border: borderSoft,
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
              }}
            >
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </Tooltip>

          <SessionDetailMenu
            anchorEl={moreEl}
            open={moreOpen}
            onClose={closeMore}
            isPaid={isPaid}
            onEdit={() => {
              closeMore();
              onClose?.();
              onEdit?.(s);
            }}
            onPayment={() => {
              closeMore();
              onPayment?.(s);
            }}
            onCancel={async () => {
              closeMore();
              await cancelSession();
            }}
          />
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.2, pb: fullScreen ? 10 : 2 }}>
        {!s ? (
          <MDTypography variant="body2" sx={{ opacity: 0.7 }}>
            Seans seçilmedi.
          </MDTypography>
        ) : (
          <MDBox sx={{ display: 'grid', gap: 1.2 }}>
            <SessionHeroCard
              s={s}
              loading={loading}
              isPaid={isPaid}
              isPackage={isPackage}
              booked={booked}
              cap={cap}
              borderSoft={borderSoft}
              onAttendanceClick={openAttendanceForSession}
            />

            <SessionParticipantsCard
              s={s}
              ps={ps}
              isPaid={isPaid}
              isPackage={isPackage}
              booked={booked}
              cap={cap}
              pCounts={pCounts}
              borderSoft={borderSoft}
              onParticipantClick={openAttendanceForParticipant}
            />

            <SessionNotesCard
              notesLoading={notesLoading}
              notes={notes}
              composerOpen={composerOpen}
              setComposerOpen={setComposerOpen}
              type={type}
              setType={setType}
              text={text}
              setText={setText}
              reminderMode={reminderMode}
              setReminderMode={setReminderMode}
              beforeMin={beforeMin}
              setBeforeMin={setBeforeMin}
              reminderAtLocal={reminderAtLocal}
              setReminderAtLocal={setReminderAtLocal}
              channel={channel}
              setChannel={setChannel}
              saving={saving}
              onSave={addNoteOrReminder}
              onDelete={removeNote}
              borderSoft={borderSoft}
            />
          </MDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, gap: 1, flexWrap: 'wrap' }}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          sx={{ borderRadius: '14px' }}
        >
          Kapat
        </MDButton>
      </DialogActions>

      <AttendanceMarkModal
        open={attOpen}
        onClose={() => {
          setAttOpen(false);
          setAttTarget(null);
        }}
        tokens={{}}
        session={{
          ...s,
          __targetName: attTarget?.fullName || null,
          attended: attTarget
            ? upper(attTarget.attendanceStatus) === 'ATTENDED'
              ? true
              : upper(attTarget.attendanceStatus) === 'NO_SHOW'
              ? false
              : null
            : s?.attended ?? null,
          cancelled: attTarget
            ? upper(attTarget.attendanceStatus) === 'CANCELLED'
            : Boolean(s?.cancelled) || upper(s?.status) === 'CANCELLED',
          attNote: attTarget?.attNote || s?.attNote || '',
        }}
        trainersById={null}
        onSave={onAttendanceSave}
      />
    </Dialog>
  );
}
